import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download01, XClose, ArrowLeft, ArrowRight, ChevronDown, Plus } from "@untitledui/icons";
import { Table } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { PerformanceModeBanner } from "@/components/app/PerformanceModeBanner";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { AppDialog } from "@/components/app/AppDialog";
import {
  useCommLogs,
  useAppLog,
  useCommLogDetail,
  statusColor,
  commLogExportUrl,
  appLogDownloadUrl,
  tryParseJson,
  toCurlBash,
  toCurlCmd,
  toPowerShell,
  toFetch,
  headersText,
  toAllJson,
  copyText,
  ENDPOINT_GROUPS,
  type CommLogFilter,
  type CommLogCursor,
  type CommLogDetail,
  type HeaderFilter,
} from "@/lib/logs";
import { useConnectors } from "@/lib/connectors";
import { formatDateTime, formatLogInstant } from "@/lib/format";
import { AppSelect } from "@/components/app/AppSelect";

type Tab = "comm" | "app";
const inputCls =
  "rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";
const REFRESH_OPTS = [
  { v: 0, l: "Off" },
  { v: 3000, l: "3s" },
  { v: 5000, l: "5s" },
  { v: 10000, l: "10s" },
];

/** datetime-local hodnota z Date. */
function dtLocal(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export function LogsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("comm");
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-display-sm font-semibold text-primary">{t("logs.title")}</h1>
        <p className="mt-1 text-md text-tertiary">{t("logs.subtitle")}</p>
      </div>
      <PerformanceModeBanner />
      <div className="flex gap-2">
        <Button size="sm" color={tab === "comm" ? "primary" : "secondary"} onPress={() => setTab("comm")}>{t("logs.tab.comm")}</Button>
        <Button size="sm" color={tab === "app" ? "primary" : "secondary"} onPress={() => setTab("app")}>{t("logs.tab.app")}</Button>
      </div>
      {tab === "comm" ? <CommLogs /> : <AppLog />}
    </div>
  );
}

function CommLogs() {
  const { t } = useTranslation();
  const connectors = useConnectors();

  // Draft filtry (aplikují se tlačítkem Filtrovat / chipem / změnou Records).
  const [search, setSearch] = useState("");
  const [authServerType, setAuthServerType] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [activeChips, setActiveChips] = useState<string[]>([]);
  const [minStatus, setMinStatus] = useState("");
  const [maxStatus, setMaxStatus] = useState("");
  const [from, setFrom] = useState(() => dtLocal(new Date(Date.now() - 864e5)));
  const [to, setTo] = useState(() => dtLocal(new Date(Date.now() + 864e5)));
  const [tokenId, setTokenId] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [headerFilters, setHeaderFilters] = useState<HeaderFilter[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [includeDetails, setIncludeDetails] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(0);

  const buildFilter = (): CommLogFilter => ({
    search, authServerType, endpoint, minStatus, maxStatus, from, to, tokenId, headerFilters,
  });

  // Aplikovaný filtr + stránkování.
  const [applied, setApplied] = useState<CommLogFilter>(() => buildFilter());
  const [cursor, setCursor] = useState<CommLogCursor>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [total, setTotal] = useState<number | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  const apply = (f?: CommLogFilter) => {
    setApplied(f ?? buildFilter());
    setCursor(null);
    setPageIndex(0);
    setTotal(null);
  };

  const { data, isLoading, isError, isFetching } = useCommLogs(
    pageSize, applied, cursor, cursor === null, cursor === null ? autoRefresh : 0,
  );
  useEffect(() => {
    if (data?.totalElements != null) setTotal(data.totalElements);
  }, [data]);

  const toggleChip = (f: string) => {
    const next = activeChips.includes(f) ? activeChips.filter((x) => x !== f) : [...activeChips, f];
    setActiveChips(next);
    const ep = next.join(",");
    setEndpoint(ep);
    apply({ ...buildFilter(), endpoint: ep });
  };

  const resetFilters = () => {
    setSearch(""); setAuthServerType(""); setEndpoint(""); setActiveChips([]);
    setMinStatus(""); setMaxStatus(""); setTokenId(""); setHeaderFilters([]);
    setFrom(dtLocal(new Date(Date.now() - 864e5)));
    setTo(dtLocal(new Date(Date.now() + 864e5)));
    apply({});
  };

  const changePageSize = (n: number) => { setPageSize(n); setCursor(null); setPageIndex(0); setTotal(null); };

  const goNext = () => {
    if (!data?.hasNext || data.nextCursorTimestamp == null || data.nextCursorId == null) return;
    setCursor({ afterTimestamp: data.nextCursorTimestamp, afterId: data.nextCursorId });
    setPageIndex((i) => i + 1);
  };
  const goPrev = () => {
    if (!data?.hasPrev) return;
    if (pageIndex <= 1) { setCursor(null); setPageIndex(0); return; }
    if (data.prevCursorTimestamp == null || data.prevCursorId == null) return;
    setCursor({ beforeTimestamp: data.prevCursorTimestamp, beforeId: data.prevCursorId });
    setPageIndex((i) => i - 1);
  };

  const setHeader = (i: number, k: "name" | "value", v: string) =>
    setHeaderFilters((hs) => hs.map((h, idx) => (idx === i ? { ...h, [k]: v } : h)));

  return (
    <div className="flex flex-col gap-4">
      {/* Filtry */}
      <div className="rounded-xl bg-primary p-4 ring-1 ring-secondary">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Field label={t("audit.search")}>
            <input className={inputCls} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && apply()} />
          </Field>
          <Field label={t("tokens.col.connector")}>
            <AppSelect fullWidth={false} value={authServerType} onChange={(e) => setAuthServerType(e.target.value)}>
              <option value="">{t("tokens.filter.all")}</option>
              {connectors.data?.map((c) => <option key={c.connectorType} value={c.connectorType}>{c.connectorType}</option>)}
            </AppSelect>
          </Field>
          <Field label={t("logs.col.endpoint")}>
            <input className={inputCls} value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="/v1/..." onKeyDown={(e) => e.key === "Enter" && apply()} />
          </Field>
          <Field label={t("logs.tokenId")}>
            <input className={inputCls} value={tokenId} onChange={(e) => setTokenId(e.target.value)} placeholder="ECT..." onKeyDown={(e) => e.key === "Enter" && apply()} />
          </Field>
          <Field label={t("logs.statusFrom")}>
            <AppSelect fullWidth={false} value={minStatus} onChange={(e) => setMinStatus(e.target.value)}>
              <option value="">–</option><option value="200">200+</option><option value="400">400+</option><option value="500">500+</option>
            </AppSelect>
          </Field>
          <Field label={t("logs.statusTo")}>
            <AppSelect fullWidth={false} value={maxStatus} onChange={(e) => setMaxStatus(e.target.value)}>
              <option value="">–</option><option value="299">299</option><option value="399">399</option><option value="499">499</option>
            </AppSelect>
          </Field>
          <Field label={t("logs.from")}>
            <input type="datetime-local" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={t("logs.to")}>
            <input type="datetime-local" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label={t("logs.records")}>
            <AppSelect fullWidth={false} value={pageSize} onChange={(e) => changePageSize(Number(e.target.value))}>
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
            </AppSelect>
          </Field>
        </div>

        {/* Endpoint chipy (multi-toggle) */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {ENDPOINT_GROUPS.map((g) => (
            <button
              key={g.filter}
              type="button"
              onClick={() => toggleChip(g.filter)}
              className={`rounded-full px-2.5 py-1 text-xs ring-1 ${
                activeChips.includes(g.filter) ? "bg-brand-solid text-white ring-transparent" : "text-secondary ring-secondary hover:bg-primary_hover"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>

        {/* Advanced: filtr podle hlaviček */}
        <div className="mt-3">
          <button type="button" className="flex items-center gap-1 text-sm font-medium text-secondary" onClick={() => setShowAdvanced((s) => !s)}>
            <ChevronDown className={`size-4 transition-transform ${showAdvanced ? "" : "-rotate-90"}`} />
            {t("logs.headerFilters")}
          </button>
          {showAdvanced && (
            <div className="mt-2 flex flex-col gap-2">
              {headerFilters.map((h, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input className={inputCls + " w-48"} placeholder="name" value={h.name} onChange={(e) => setHeader(i, "name", e.target.value)} />
                  <span className="text-tertiary">=</span>
                  <input className={inputCls + " flex-1"} placeholder="value" value={h.value} onChange={(e) => setHeader(i, "value", e.target.value)} />
                  <Button size="sm" color="tertiary" iconLeading={XClose} aria-label="remove" onPress={() => setHeaderFilters((hs) => hs.filter((_, idx) => idx !== i))} />
                </div>
              ))}
              <div>
                <Button size="sm" color="secondary" iconLeading={Plus} onPress={() => setHeaderFilters((hs) => [...hs, { name: "", value: "" }])}>
                  {t("logs.addHeader")}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Akce + auto-refresh */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button size="sm" color="primary" onPress={() => apply()}>{t("logs.filter")}</Button>
          <Button size="sm" color="secondary" onPress={resetFilters}>{t("logs.reset")}</Button>
          <label className="ml-2 flex items-center gap-1.5 text-sm text-tertiary">
            {t("logs.autoRefresh")}
            <AppSelect fullWidth={false} value={autoRefresh} onChange={(e) => setAutoRefresh(Number(e.target.value))}>
              {REFRESH_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </AppSelect>
          </label>
        </div>
      </div>

      {/* Toolbar: počet + export */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-tertiary">
          {data?.success ? `${data.logs.length} ${t("logs.results")}${total != null ? ` / ~${total.toLocaleString()}` : ""}` : ""}
        </p>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-tertiary">
            <input type="checkbox" checked={includeDetails} onChange={(e) => setIncludeDetails(e.target.checked)} className="size-4" />
            {t("logs.includeDetails")}
          </label>
          <Button size="sm" color="secondary" iconLeading={Download01} onPress={() => window.open(commLogExportUrl("json", applied, includeDetails), "_blank")}>JSON</Button>
          <Button size="sm" color="secondary" iconLeading={Download01} onPress={() => window.open(commLogExportUrl("csv", applied, includeDetails), "_blank")}>CSV</Button>
        </div>
      </div>

      {isLoading && <LoadingIndicator />}
      {(isError || (data && !data.success)) && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data?.success && (
        <>
          <div className="overflow-hidden rounded-xl ring-1 ring-secondary">
            <Table aria-label={t("logs.tab.comm")} selectionMode="none">
              <Table.Header>
                <Table.Head isRowHeader id="timestamp" label={t("txlogs.col.created")} />
                <Table.Head id="authServerType" label={t("logs.col.server")} />
                <Table.Head id="method" label={t("logs.col.method")} />
                <Table.Head id="endpoint" label={t("logs.col.endpoint")} />
                <Table.Head id="statusCode" label={t("logs.col.status")} />
                <Table.Head id="durationMs" label={t("txlogs.col.duration")} />
                <Table.Head id="tokenId" label={t("logs.col.token")} />
              </Table.Header>
              <Table.Body items={data.logs}>
                {(row) => (
                  <Table.Row id={String(row.id)} onAction={() => setDetailId(row.id)} className="cursor-pointer">
                    <Table.Cell className="whitespace-nowrap">{formatDateTime(row.timestamp, { seconds: true })}</Table.Cell>
                    <Table.Cell>{row.authServerType}</Table.Cell>
                    <Table.Cell className="font-medium text-primary">{row.method ?? "—"}</Table.Cell>
                    <Table.Cell className="max-w-100 truncate" title={row.endpoint ?? ""}>{row.endpoint ?? "—"}</Table.Cell>
                    <Table.Cell>
                      <Badge type="pill-color" size="sm" color={statusColor(row.statusCode)}>{row.statusCode ?? "ERR"}</Badge>
                    </Table.Cell>
                    <Table.Cell>{row.durationMs != null ? `${row.durationMs} ms` : "—"}</Table.Cell>
                    <Table.Cell className="font-mono text-xs">{row.tokenId ? row.tokenId.slice(0, 12) + "…" : "—"}</Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button size="sm" color="secondary" iconLeading={ArrowLeft} isDisabled={!data.hasPrev || isFetching} onPress={goPrev}>{t("pager.prev")}</Button>
              <span className="text-sm text-tertiary">
                {t("logs.page")} {pageIndex + 1}{total != null ? ` · ~${total.toLocaleString()} ${t("logs.total")}` : ""}
              </span>
              <Button size="sm" color="secondary" iconTrailing={ArrowRight} isDisabled={!data.hasNext || isFetching} onPress={goNext}>{t("pager.next")}</Button>
            </div>
          </div>
        </>
      )}

      {detailId != null && <CommLogDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-tertiary">{label}</span>
      {children}
    </label>
  );
}

const COPY_ACTIONS: { key: string; label: string; build: (d: CommLogDetail) => string }[] = [
  { key: "curl-bash", label: "cURL (bash)", build: toCurlBash },
  { key: "curl-cmd", label: "cURL (cmd)", build: toCurlCmd },
  { key: "powershell", label: "PowerShell", build: toPowerShell },
  { key: "fetch", label: "fetch", build: toFetch },
  { key: "req-headers", label: "Request headers", build: (d) => headersText(d.requestHeaders) },
  { key: "res-headers", label: "Response headers", build: (d) => headersText(d.responseHeaders) },
  { key: "all-json", label: "Vše jako JSON", build: toAllJson },
];

function CommLogDetailDialog({ id, onClose }: { id: number; onClose: () => void }) {
  const { t } = useTranslation();
  const { data, isLoading } = useCommLogDetail(id);
  const [menu, setMenu] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const log = data?.log;

  const doCopy = async (build: (d: CommLogDetail) => string) => {
    if (!log) return;
    const ok = await copyText(build(log));
    setMenu(false);
    setCopied(ok ? "ok" : "err");
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <AppDialog isOpen onClose={onClose} size="3xl" title={t("logs.detailTitle", { id })}>
      {isLoading && <LoadingIndicator />}
      {log && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Button size="sm" color="secondary" iconTrailing={ChevronDown} onPress={() => setMenu((m) => !m)}>{t("logs.copy")}</Button>
              {menu && (
                <div className="absolute z-10 mt-1 flex w-56 flex-col rounded-lg bg-primary p-1 shadow-lg ring-1 ring-secondary">
                  {COPY_ACTIONS.map((a) => (
                    <button key={a.key} type="button" className="rounded-md px-3 py-1.5 text-left text-sm text-secondary hover:bg-primary_hover" onClick={() => void doCopy(a.build)}>
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {copied && <span className={`text-sm ${copied === "err" ? "text-error-primary" : "text-success-primary"}`}>{copied === "err" ? t("logs.copyFail") : t("logs.copied")}</span>}
          </div>
          <div className="text-sm text-secondary">
            <span className="font-mono font-medium text-primary">{log.method} {log.endpoint}</span>
            {log.statusCode != null && <span className="ml-2">→ {log.statusCode}</span>}
            {log.durationMs != null && <span className="ml-2 text-tertiary">{log.durationMs} ms</span>}
          </div>
          <Block label={t("logs.reqHeaders")} value={log.requestHeaders} />
          <Block label={t("logs.reqBody")} value={log.requestData} />
          <Block label={t("logs.resHeaders")} value={log.responseHeaders} />
          <Block label={t("logs.resBody")} value={log.responseData} />
        </div>
      )}
    </AppDialog>
  );
}

function Block({ label, value }: { label: string; value: unknown }) {
  const parsed = tryParseJson(value);
  if (parsed == null || parsed === "") return null;
  const text = typeof parsed === "string" ? parsed : JSON.stringify(parsed, null, 2);
  return (
    <div>
      <div className="mb-1 text-xs font-semibold text-tertiary">{label}</div>
      <pre className="max-h-[40vh] overflow-auto rounded-md bg-secondary p-2 font-mono text-xs text-secondary ring-1 ring-secondary">{text}</pre>
    </div>
  );
}

function AppLog() {
  const { t } = useTranslation();
  const [lines, setLines] = useState(500);
  const [level, setLevel] = useState("");
  const [filterInput, setFilterInput] = useState("");
  const [filter, setFilter] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const { data, isLoading, isError } = useAppLog(lines, level || undefined, filter || undefined, autoRefresh);
  const preRef = useRef<HTMLPreElement>(null);

  // Auto-scroll na konec při nových datech (když je zapnutý).
  useEffect(() => {
    if (autoScroll && preRef.current) preRef.current.scrollTop = preRef.current.scrollHeight;
  }, [data, autoScroll]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t("logs.app.lines")}>
          <AppSelect fullWidth={false} value={lines} onChange={(e) => setLines(Number(e.target.value))}>
            {[100, 200, 500, 1000, 2000].map((n) => <option key={n} value={n}>{n}</option>)}
          </AppSelect>
        </Field>
        <Field label={t("logs.app.level")}>
          <AppSelect fullWidth={false} value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="">{t("tokens.filter.all")}</option>
            {["ERROR", "WARN", "INFO", "DEBUG"].map((l) => <option key={l} value={l}>{l}</option>)}
          </AppSelect>
        </Field>
        <Field label={t("logs.app.filter")}>
          <input className={inputCls} value={filterInput} onChange={(e) => setFilterInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setFilter(filterInput)} />
        </Field>
        <Button size="sm" color="primary" onPress={() => setFilter(filterInput)}>{t("logs.app.load")}</Button>
        <label className="flex items-center gap-1.5 text-sm text-tertiary">
          {t("logs.autoRefresh")}
          <AppSelect fullWidth={false} value={autoRefresh} onChange={(e) => setAutoRefresh(Number(e.target.value))}>
            {REFRESH_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </AppSelect>
        </label>
        <label className="flex items-center gap-1.5 text-sm text-tertiary">
          <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} className="size-4" />
          {t("logs.app.autoScroll")}
        </label>
        <Button size="sm" color="secondary" iconLeading={Download01} onPress={() => window.open(appLogDownloadUrl(), "_blank")}>{t("logs.app.download")}</Button>
      </div>
      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}
      {data && (
        <>
          <p className="text-xs text-tertiary">{t("logs.app.lineCount", { count: data.entries.length, max: lines })}</p>
          <pre ref={preRef} className="max-h-[60vh] overflow-auto rounded-xl bg-secondary p-4 font-mono text-xs leading-relaxed ring-1 ring-secondary">
            {data.entries.length > 0
              ? data.entries.map((e, i) => (
                  <div key={i} className={appLogLevelColor(e.level)}>
                    {`${formatLogInstant(e.timestamp)} ${e.level.padStart(5)} [${e.thread}] ${e.logger} : ${e.message}`}
                  </div>
                ))
              : <span className="text-secondary">{t("logs.empty")}</span>}
          </pre>
        </>
      )}
    </div>
  );
}

/** Barva řádku aplikačního logu dle úrovně. */
function appLogLevelColor(level: string): string {
  switch (level.toUpperCase()) {
    case "ERROR": return "text-error-primary";
    case "WARN": return "text-warning-primary";
    case "DEBUG":
    case "TRACE": return "text-tertiary";
    default: return "text-secondary";
  }
}
